// useNoncompliances.js
// ─────────────────────────────────────────────────────────────────────────────
// Custom hook that owns all data-fetching and derived state for the
// Noncompliances modal. noncompliances.js imports this hook and renders
// purely from what it returns - no fetch logic lives in the component.
//
// ── Why a dedicated hook? ──────────────────────────────────────────────────────
//
// The modal coordinates FOUR independent API calls:
//
//   1. fetchNoncompliances()            - seed: top box counts + default mod list
//   2. fetchNoncompliancesFullList()    - search corpus (fetched once on first search)
//   3. fetchNoncompliancesDetail()      - per-mod detail (fetched on each selection)
//   4. fetchNoncompliancesMetricTasks() - metric drill-down items (fetched on box click)
//
// Each call has its own loading/error lifecycle, and several derived states
// (filtered list, active metric rows) depend on combinations of them.
// Keeping this in the component would make it unwieldy; the hook provides a
// clean, testable boundary.
//
// ── Lazy-load strategy for fullList ───────────────────────────────────────────
//
// The full manufactured-items list can be large and is only needed when the
// user starts typing in the search box. We therefore defer fetching it until
// the first non-empty search query, then cache the result in STATE for the
// remainder of the modal session. Subsequent keystrokes filter in memory - no
// extra calls.
//
// NOTE: fullList is stored in STATE (not a ref) on purpose. A ref does not
// trigger a re-render when it is populated, so the filtered-list useMemo would
// not re-run on the render where the fetch resolves - causing the "type '12'
// returns nothing until you type '12w'" bug. State fixes this: when the fetch
// resolves, fullList updates, the memo re-runs, and results appear immediately.
//
// ── Filtering / ordering strategy ─────────────────────────────────────────────
//
// listItems is derived PURELY from `search`, `onlineMods`, and `fullList` -
// never from any async status flag. This guarantees the rendered list always
// matches the current search box value on the same render (no stale frames):
//
//   • Empty query  → online-mods seed list. When onlineMods is [], listItems is
//                     [] and the component shows the "No modules On Line" state.
//   • Non-empty    → ranked substring filter over the full corpus, ordered by
//                     closeness of match (exact > prefix > substring by offset)
//                     with WoNum > BatchNum > ItemNum field priority.
//
// There is deliberately NO code path that shows the full corpus without a
// search query - the corpus only ever appears filtered by an active query.
//
// ── Metric-tasks fetch strategy ───────────────────────────────────────────────
//
// Each metric box (completed / unevaluated / nonconformed) fetches its items
// on demand the first time the box is clicked. Results are cached in a ref map
// keyed by metric name so re-opening the same panel never re-fetches.
//
// ── AbortController cleanup ───────────────────────────────────────────────────
//
// Every fetch is tied to an AbortController whose signal is passed into the
// dataReceiver functions. The controller is aborted in the useEffect cleanup
// so in-flight requests don't update state after the component unmounts or a
// new fetch supersedes the previous one (detail fetch on rapid mod-clicks).
//
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchNoncompliances,
  fetchNoncompliancesDetail,
  fetchNoncompliancesFullList,
  fetchNoncompliancesMetricTasks,
} from "../../utils/dataReceiver";

// ─── State shape constants ────────────────────────────────────────────────────

/** @typedef {'idle'|'loading'|'success'|'error'} FetchStatus */

const IDLE = "idle";
const LOADING = "loading";
const SUCCESS = "success";
const ERROR = "error";

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useNoncompliances
 *
 * Call once at the top of the Noncompliances modal component.
 *
 * @returns {{
 *   // ── Seed (top boxes + default list) ─────────────────────────────────────
 *   seedStatus: FetchStatus,
 *   seedError: string | null,
 *   jobsCC: number,
 *   jobsUE: number,
 *   jobsNC: number,
 *
 *   // ── Left-panel list ──────────────────────────────────────────────────────
 *   listItems: Array<{ WoNum, ItemNum, BatchNum, dateLabel: string }>,
 *   listStatus: FetchStatus,
 *   listError: string | null,
 *   hasSearch: boolean,        - true when the search box holds a non-empty query
 *   hasOnlineMods: boolean,    - true when seed returned ≥1 online mod
 *
 *   // ── Search ───────────────────────────────────────────────────────────────
 *   search: string,
 *   setSearch: (val: string) => void,
 *   isSearching: boolean,
 *
 *   // ── Mod selection ────────────────────────────────────────────────────────
 *   selectedBatchNum: string | null,
 *   selectMod: (batchNum: string) => void,
 *
 *   // ── Detail panel ─────────────────────────────────────────────────────────
 *   detailStatus: FetchStatus,
 *   detailError: string | null,
 *   detailData: object | null,
 *
 *   // ── Station / task drill-down (UI convenience state) ─────────────────────
 *   selectedStationId: number | null,
 *   setSelectedStationId: (id: number | null) => void,
 *   selectedTaskId: string | null,
 *   setSelectedTaskId: (id: string | null) => void,
 *
 *   // ── Metric box drill-down ─────────────────────────────────────────────────
 *   activeMetric: string | null,
 *   setActiveMetric: (key: string | null) => void,
 *
 *   // ── Metric-tasks (populated when a metric box is open) ───────────────────
 *   metricTasksStatus: FetchStatus,
 *   metricTasksError: string | null,
 *   metricTasksItems: Array, - items[] from fetchNoncompliancesMetricTasks
 * }}
 */
export function useNoncompliances() {
  // ── 1. Seed state ────────────────────────────────────────────────────────────
  const [seedStatus, setSeedStatus] = useState(LOADING);
  const [seedError, setSeedError] = useState(null);
  const [jobsCC, setJobsCC] = useState(0);
  const [jobsUE, setJobsUE] = useState(0);
  const [jobsNC, setJobsNC] = useState(0);
  const [onlineMods, setOnlineMods] = useState([]);

  // ── 2. Full-list corpus (loaded lazily on first search) ──────────────────────
  // Stored in STATE (not a ref) so populating it re-runs the filter useMemo on
  // the same render the fetch resolves. See header note for the bug this fixes.
  const [fullList, setFullList] = useState(null); // null = not yet fetched
  const fullListLoadedRef = useRef(false); // guards against duplicate fetches
  const [listStatus, setListStatus] = useState(IDLE);
  const [listError, setListError] = useState(null);

  // ── 3. Search ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");

  // ── 4. Selection ─────────────────────────────────────────────────────────────
  const [selectedBatchNum, setSelectedBatchNum] = useState(null);

  // ── 5. Detail state ──────────────────────────────────────────────────────────
  const [detailStatus, setDetailStatus] = useState(IDLE);
  const [detailError, setDetailError] = useState(null);
  const [detailData, setDetailData] = useState(null);

  // ── 6. Sub-panel navigation ──────────────────────────────────────────────────
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null);

  // ── 7. Metric-tasks drill-down ───────────────────────────────────────────────
  // Cache keyed by metric name so each panel only fetches once per session.
  const metricTasksCacheRef = useRef({}); // { completed: [], unevaluated: [], nonconformed: [] }
  const [metricTasksStatus, setMetricTasksStatus] = useState(IDLE);
  const [metricTasksError, setMetricTasksError] = useState(null);
  const [metricTasksItems, setMetricTasksItems] = useState([]);

  // ─── Fetch seed on mount ─────────────────────────────────────────────────────

  useEffect(() => {
    const controller = new AbortController();

    setSeedStatus(LOADING);
    setSeedError(null);

    fetchNoncompliances(controller.signal)
      .then((data) => {
        setJobsCC(data.jobsCC);
        setJobsUE(data.jobsUE);
        setJobsNC(data.jobsNC);
        setOnlineMods(Array.isArray(data.onlineMods) ? data.onlineMods : []);
        setSeedStatus(SUCCESS);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setSeedError(err.message ?? "Failed to load noncompliance data.");
        setSeedStatus(ERROR);
      });

    return () => controller.abort();
  }, []);

  // ─── Fetch full list (lazily, on first search keystroke) ─────────────────────
  //
  // Fires the first time the user types a non-empty query. Once loaded, the
  // corpus lives in `fullList` state for the rest of the session and every
  // subsequent keystroke filters it in memory (see the listItems useMemo).
  //
  // Clearing the search box (empty query) resets listStatus to IDLE but leaves
  // the loaded corpus intact so re-searching is instant.

  useEffect(() => {
    const q = search.trim();

    // Empty query - reset list lifecycle to idle. The listItems memo will fall
    // back to onlineMods. Corpus stays cached in `fullList`.
    if (!q) {
      setListError(null);
      setListStatus(IDLE);
      return;
    }

    // Corpus already loaded - just mark success so the memo filters immediately.
    if (fullListLoadedRef.current) {
      setListStatus(SUCCESS);
      return;
    }

    const controller = new AbortController();
    setListStatus(LOADING);
    setListError(null);

    fetchNoncompliancesFullList(controller.signal)
      .then((rows) => {
        fullListLoadedRef.current = true;
        setFullList(Array.isArray(rows) ? rows : []);
        setListStatus(SUCCESS);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setListError(err.message ?? "Failed to load module list.");
        setListStatus(ERROR);
      });

    return () => controller.abort();
  }, [search]);

  // ─── Fetch detail when selection changes ─────────────────────────────────────

  useEffect(() => {
    if (!selectedBatchNum) {
      setDetailData(null);
      setDetailStatus(IDLE);
      setDetailError(null);
      setSelectedStationId(null);
      setSelectedTaskId(null);
      return;
    }

    const controller = new AbortController();
    setDetailStatus(LOADING);
    setDetailError(null);
    setDetailData(null);
    setSelectedStationId(null);
    setSelectedTaskId(null);

    fetchNoncompliancesDetail({ batchNum: selectedBatchNum }, controller.signal)
      .then((data) => {
        setDetailData(data);
        setDetailStatus(SUCCESS);
        const firstStation = data?.stations?.[0];
        if (firstStation) setSelectedStationId(firstStation.stationId);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setDetailError(err.message ?? "Failed to load module details.");
        setDetailStatus(ERROR);
      });

    return () => controller.abort();
  }, [selectedBatchNum]);

  // ─── Fetch metric-tasks when a metric box is opened ──────────────────────────
  //
  // Fires whenever activeMetric becomes non-null.
  // Results are cached by metric key - re-clicking the same box never re-fetches.
  // Closing the panel (activeMetric → null) leaves the cache intact so it
  // renders instantly if the user re-opens the same panel later in the session.

  useEffect(() => {
    if (!activeMetric) {
      // Panel closed - leave existing items visible until next open so there
      // is no flash of empty content if the user quickly re-opens.
      return;
    }

    // Already cached - hydrate state immediately, no fetch needed.
    if (metricTasksCacheRef.current[activeMetric] !== undefined) {
      setMetricTasksItems(metricTasksCacheRef.current[activeMetric]);
      setMetricTasksStatus(SUCCESS);
      setMetricTasksError(null);
      return;
    }

    // Not yet cached - fetch from the API.
    const controller = new AbortController();
    setMetricTasksStatus(LOADING);
    setMetricTasksError(null);
    setMetricTasksItems([]);

    fetchNoncompliancesMetricTasks({ metric: activeMetric }, controller.signal)
      .then((data) => {
        metricTasksCacheRef.current[activeMetric] = data.items;
        setMetricTasksItems(data.items);
        setMetricTasksStatus(SUCCESS);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setMetricTasksError(err.message ?? "Failed to load metric details.");
        setMetricTasksStatus(ERROR);
      });

    return () => controller.abort();
  }, [activeMetric]);

  // ─── Reset task selection when station changes ───────────────────────────────

  useEffect(() => {
    setSelectedTaskId(null);
  }, [selectedStationId]);

  // ─── Close metric drill-down when search is active ───────────────────────────

  useEffect(() => {
    if (search) setActiveMetric(null);
  }, [search]);

  // ─── Derive left-panel list items ────────────────────────────────────────────
  //
  // Fully dynamic, derived purely from `search`, `onlineMods`, and `fullList`.
  // No dependency on async listStatus - the displayed list always matches the
  // current search box value on the same render.
  //
  //   • Empty query  → online-mods seed list (empty [] when none are on line).
  //   • Non-empty    → ranked substring filter over the full corpus, ordered by
  //                     closeness of match (exact > prefix > substring by
  //                     offset) with WoNum > BatchNum > ItemNum field priority.

  const listItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    // Build a display row with a GUARANTEED-unique, stable React key.
    // BatchNum can be null/duplicate in the corpus, which breaks reconciliation
    // (stale <button> nodes persist). We derive `key` from all identifying
    // fields plus the array index so it is always unique and stable per render.
    const toRow = (m, i, dateLabel) => ({
      key: `${m.WoNum ?? "x"}|${m.BatchNum ?? "x"}|${m.ItemNum ?? "x"}|${i}`,
      WoNum: m.WoNum ?? "-",
      ItemNum: m.ItemNum ?? "-",
      BatchNum: m.BatchNum ?? "-",
      dateLabel,
    });

    // ── Empty search: reset to online mods ──────────────────────────────────
    if (!q) {
      return onlineMods.map((m, i) =>
        toRow(
          m,
          i,
          m.actualOnlineDate
            ? new Date(m.actualOnlineDate).toLocaleDateString()
            : (m.scheduledOnlineDate ?? "-"),
        ),
      );
    }

    // ── Active search: ranked filter over the full corpus ───────────────────
    const source = fullList ?? [];

    const fieldScore = (raw) => {
      const v = (raw ?? "").toLowerCase();
      if (!v) return -1;
      if (v === q) return 0;
      const idx = v.indexOf(q);
      if (idx === -1) return -1;
      if (idx === 0) return 1;
      return 2 + idx / 100;
    };

    const ranked = [];
    for (const m of source) {
      const woScore = fieldScore(m.WoNum);
      const batchScore = fieldScore(m.BatchNum);
      const itemScore = fieldScore(m.ItemNum);

      const candidates = [];
      if (woScore >= 0) candidates.push(woScore);
      if (batchScore >= 0) candidates.push(batchScore + 0.001);
      if (itemScore >= 0) candidates.push(itemScore + 0.002);

      if (candidates.length === 0) continue;

      ranked.push({ mod: m, score: Math.min(...candidates) });
    }

    ranked.sort(
      (a, b) =>
        a.score - b.score ||
        (a.mod.WoNum ?? "").localeCompare(b.mod.WoNum ?? ""),
    );

    return ranked.map(({ mod: m }, i) =>
      toRow(
        m,
        i,
        m.CreatedAt ? new Date(m.CreatedAt).toLocaleDateString() : "-",
      ),
    );
  }, [search, onlineMods, fullList]);

  // ─── selectMod callback ───────────────────────────────────────────────────────

  const selectMod = useCallback((batchNum) => {
    setSelectedBatchNum(batchNum);
    setActiveMetric(null);
  }, []);

  // ─── Derived flags for context-specific empty states ─────────────────────────

  const hasSearch = search.trim().length > 0;
  const hasOnlineMods = onlineMods.length > 0;

  // ─── Return ───────────────────────────────────────────────────────────────────

  return {
    // Seed
    seedStatus,
    seedError,
    jobsCC,
    jobsUE,
    jobsNC,

    // List
    listItems,
    listStatus,
    listError,
    hasSearch,
    hasOnlineMods,

    // Search
    search,
    setSearch,
    isSearching: listStatus === LOADING,

    // Selection
    selectedBatchNum,
    selectMod,

    // Detail
    detailStatus,
    detailError,
    detailData,

    // Sub-panel nav
    selectedStationId,
    setSelectedStationId,
    selectedTaskId,
    setSelectedTaskId,

    // Metric drill-down
    activeMetric,
    setActiveMetric,

    // Metric-tasks
    metricTasksStatus,
    metricTasksError,
    metricTasksItems,
  };
}
